/**
 * Client-side encryption for chat attachments (WebCrypto AES-256-GCM).
 *
 * Files are encrypted with a per-channel key before upload, so the public-read
 * storage objects are opaque ciphertext rather than files anyone holding the URL
 * can fetch. That is the threat this closes, and it closes it. Wire format:
 * 12-byte random IV prepended to the ciphertext, one opaque
 * application/octet-stream blob.
 *
 * THIS IS NOT END-TO-END ENCRYPTION, and must not be described as such.
 *
 * This header used to bound decryption to channel membership. That was true only
 * in the sense that the server decides who is a member — and the server minted
 * the key it handed them. There is no key exchange, no per-user keypair, no
 * fingerprint, and nothing is verified. The AES key arrives from the Apiboo
 * backend (`resp.channel_key` on the socket join, or `GET
 * /chat/channels/:id/key`); the client never generates it and never checks it.
 *
 * So, accurately: attachments are **encrypted at rest in object storage under a
 * server-managed per-channel key**. Apiboo can read every attachment, and a
 * compromised server could hand a victim a substituted key with no client-side
 * signal. Key rotation on membership change is likewise invisible here —
 * `chatStore.channelKeys` has no version or epoch, so a removed member's cached
 * key keeps working against old ciphertext.
 *
 * The primitives themselves are used correctly: AES-GCM, a fresh 12-byte random
 * IV per file, non-extractable `importKey`. The gap is architectural, not an
 * implementation bug, and making it genuinely end-to-end means giving each
 * account an identity keypair and wrapping the channel key to each member —
 * designed but not built (Documentation/audit-2026-08-03/chat-e2e-design.md).
 *
 * Genuinely end-to-end elsewhere in the app, for contrast, and where the phrase
 * IS accurate: vault secret links (`utils/secretLinkCrypto.js`, key lives only
 * in the URL fragment) and vault shares (`utils/vaultShareCrypto.js`, sealed to
 * each recipient's published key).
 */
import chatService from '../services/chatService';
import { useChatStore } from '../store/chatStore';

// base64 key -> Promise<CryptoKey>; keys are imported non-extractable and
// only ever live in memory.
const importedKeys = new Map();

export function importChannelKey(b64) {
  if (!importedKeys.has(b64)) {
    const raw = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    importedKeys.set(
      b64,
      crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
    );
  }
  return importedKeys.get(b64);
}

/**
 * The channel's CryptoKey: from the store (populated by the WS join reply)
 * or, as a fallback, fetched over REST — so history images decrypt even
 * before/without a live socket.
 */
export async function getChannelCryptoKey(channelId) {
  let b64 = useChatStore.getState().channelKeys[channelId];
  if (!b64) {
    b64 = await chatService.getChannelKey(channelId);
    useChatStore.getState().setChannelKey(channelId, b64);
  }
  return importChannelKey(b64);
}

/** Encrypt a File/Blob -> Blob([iv, ciphertext]) ready for upload. */
export async function encryptFile(cryptoKey, file) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, await file.arrayBuffer());
  return new Blob([iv, ct], { type: 'application/octet-stream' });
}

/** Decrypt a downloaded ArrayBuffer back into a Blob of the original mime. */
export async function decryptBuffer(cryptoKey, buf, mime) {
  const bytes = new Uint8Array(buf);
  const iv = bytes.slice(0, 12);
  const ct = bytes.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, ct);
  return new Blob([pt], { type: mime || 'application/octet-stream' });
}
